/* istanbul ignore file */
import { useCallback, useContext } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useCreateHookResultPromise } from '@bingads-webui-campaign-react/common-hooks';
import { useSuggestionsContext } from '@bingads-webui-campaign-react/pmax-suggestion-service';

import { assetGroupViewModelFromSuggestions } from './model';
import { useCreateTempAssetGroup } from './use-create-temp-asset-group';
import { useAdAssetsInstrument } from './use-ad-assets-instrument';

export const useReadFromSuggestions = ({
  createAssetGroupWhileLoading,
  onboardingUrlSuggestion,
}) => {
  const { scope } = useContext(ScopeContext);
  const serverContext = useContext(ServerContext);

  const {
    campaignId, assetGroupId,
  } = scope.values;

  const uiActivity = useAdAssetsInstrument();
  const createAssetGroupWithTempAssets = useCreateTempAssetGroup();

  const {
    isImagesDone,
    isRSADone,
    isBIDone,
    images: preLoadedImagesSuggestions,
    rsa: preLoadedRSASuggestions,
    bi: preLoadedBISuggestions,
  } = useSuggestionsContext();

  const createImagesSuggestionPromise = useCreateHookResultPromise({
    result: isImagesDone && (preLoadedImagesSuggestions || []),
  });
  const createRSASuggestionPromise = useCreateHookResultPromise({
    result: isRSADone && (preLoadedRSASuggestions || []),
  });
  const createBISuggestionPromise = useCreateHookResultPromise({
    result: isBIDone && (preLoadedBISuggestions || {}),
  });

  const readFromSuggestions = useCallback(() => Promise.all([
    createImagesSuggestionPromise(),
    createRSASuggestionPromise(),
    createBISuggestionPromise(),
  ]).then(([
    imagesSuggestions,
    rsaSuggestions,
    biSuggestions,
  ]) => {
    const assetGroupData = assetGroupViewModelFromSuggestions({
      onboardingUrl: onboardingUrlSuggestion,
      imagesSuggestions,
      rsaSuggestions,
      biSuggestions,
    });

    if (createAssetGroupWhileLoading && !assetGroupId) {
      return createAssetGroupWithTempAssets(assetGroupData, serverContext, campaignId);
    }

    return {
      onboardingUrl: onboardingUrlSuggestion,
      assetGroupData: {
        ...assetGroupData,
        FinalUrlForRecommendation: assetGroupData.FinalUrls[0],
      },
    };
  }).catch(/* istanbul ignore next */ (err) => {
    uiActivity.error(`[Ad assets step] Init from suggestions failed: ${JSON.stringify(err)}`);

    return Promise.reject(err);
  }), [assetGroupId, campaignId, createAssetGroupWhileLoading, createAssetGroupWithTempAssets,
    createBISuggestionPromise, createImagesSuggestionPromise, createRSASuggestionPromise,
    onboardingUrlSuggestion, serverContext, uiActivity]);

  return readFromSuggestions;
};
